import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import Card from "../../components/Card/Card";
import { constants } from "../../redux/providers/actions";
import { commonConstants } from "../../redux/common/actions";
import ReactTable from "react-table";
import { filterMethods } from "../utils/filtersAndCells";
import Button from "../../components/CustomButton/CustomButton.jsx";
import NotificationSystem from "react-notification-system";
import { style as notificationStyling } from "../../variables/Variables.jsx";
import { Link } from "react-router-dom";
import { getFormattedUser } from "../../services/auth";
import CustomReactTablePagination from "../Components/CustomReactTablePagination";
import AccessDeniedPage from "components/AccessDeniedPage";
import getProviderColumns from "./providersColumns";
import DownloadProviderListButton from "./components/DownloadProviderListButton";
import DeleteProviderAlert from "./components/DeleteProviderAlert";
import DisclaimerPopup from "./components/DisclaimerPopup";

const {
  FETCH_PROVIDER_LIST,
  DELETE_PROVIDER,
  UPDATE_PROVIDER_LIST_FILTERS,
  RESET_PROVIDER_LIST_FILTERS,
} = constants;
const { REQUEST } = commonConstants;

const Providers = ({ history }) => {
  const {
    list,
    loading,
    error = {},
    success = {},
    tableState,
  } = useSelector((state) => state.providers);
  const dispatch = useDispatch();
  const notificationSystem = useRef();
  const [alert, setAlert] = useState(null);
  const [_notificationSystem, setNotificationSystem] = useState({});
  const [showDisclaimer, setShowDisclaimer] = useState(true);

  // initial page fetch
  useEffect(() => {
    dispatch({
      type: FETCH_PROVIDER_LIST + REQUEST,
      providerType: "providers",
      source: FETCH_PROVIDER_LIST,
    });
    setNotificationSystem(notificationSystem);
    return () => {};
  }, []);

  // retrieve admin privilege/user if applicable
  const [hasAdmin, setHasAdmin] = useState(false);
  const [, setSelectedScheduleUser] = useState(null);
  useEffect(() => {
    const getAuth = async () => {
      const user = await getFormattedUser();
      const hasAdmin =
        user &&
        user.roles &&
        (user.roles.includes("SuperAdmin") ||
          user.roles.includes("HrAdmin") ||
          user.roles.includes("ProviderAdmin") ||
          user.roles.includes("ProviderQuestionnaireAdmin"));
      setHasAdmin(hasAdmin);
      setSelectedScheduleUser(user.id);
    };
    getAuth().then();
  }, []);

  // handle ajax side effects
  useEffect(() => {
    const notificationSystem = _notificationSystem.current;
    if (notificationSystem && success.message) {
      notificationSystem.addNotification({
        message: success.message,
        level: "info",
        position: "tc",
      });
    }
    if (notificationSystem && error.message) {
      notificationSystem.addNotification({
        message: error.message,
        level: "error",
        position: "tc",
      });
    }
    return () => {};
  }, [success.message, error.message]);

  // handle onClick row navigation
  const [historyNavigation, setHistoryNavigation] = useState({
    enabled: false,
    id: null,
  });
  useEffect(() => {
    if (historyNavigation.enabled) {
      history.push(`/admin/provider/${historyNavigation.id}?readOnly=true`);
    }
    return () => {};
  }, [historyNavigation.enabled]);

  // handle scroll to top for notifications
  useEffect(() => {
    // THIS IS SCREWY - Desktop works fine, Mobile scrolls to top quickly then back to original position - 2x fixed this
    document.getElementById("top-of-the-form-to-ya").scrollIntoView(true);
    document.getElementById("top-of-the-form-to-ya").scrollIntoView(true);
    return () => {};
  }, [success.message, error.message]);

  const { startsWithFilterMethod } = filterMethods();

  const formattedList = useMemo(() => {
    return list.map((l) => {
      return {
        ...l,
        dateVerified: moment(l.dateVerified).format("MMMM, Do YYYY"),
      };
    });
  }, [list]);

  const onDeleteClickHandler = (providerName, id) => {
    setAlert(
      <DeleteProviderAlert
        providerName={providerName}
        onConfirm={() => {
          dispatch({ type: DELETE_PROVIDER + REQUEST, payload: { id } });
          setAlert(null);
        }}
        onCancel={() => setAlert(null)}
      />
    );
  }

  const handleClearFilters = () => {
    dispatch({
      type: RESET_PROVIDER_LIST_FILTERS,
    });
  };

  const handleUpdateFilter = (field) => (value) => {
    dispatch({
      type: UPDATE_PROVIDER_LIST_FILTERS,
      field,
      value,
    });
  };

  const tableColumns = useMemo(
    () => getProviderColumns(hasAdmin),
    [hasAdmin]
  );

  if (error && error.statusCode === 403) {
    return <AccessDeniedPage/>;
  }

  return (
    <div className="main-content">
      {showDisclaimer && (
        <DisclaimerPopup onConfirm={() => setShowDisclaimer(false)} />
      )}
      <NotificationSystem
        ref={notificationSystem}
        style={notificationStyling}
      />
      {alert}
      <Card
        content={
          <div>
            <DownloadProviderListButton data={formattedList} hasAdmin={hasAdmin}/>

            <ReactTable
              PaginationComponent={CustomReactTablePagination}
              paginationProps={{
                onClearFilters: handleClearFilters,
              }}
              filtered={tableState.filtered}
              onFilteredChange={handleUpdateFilter("filtered")}
              sorted={tableState.sorted}
              onSortedChange={handleUpdateFilter("sorted")}
              page={tableState.page}
              onPageChange={handleUpdateFilter("page")}
              pageSize={tableState.pageSize}
              onPageSizeChange={handleUpdateFilter("pageSize")}
              showTotalPages={true}
              canNextFromData={false}
              data={formattedList.map((prop) => {
                return {
                  id: prop.id,
                  name: prop.name,
                  email: prop.email,
                  providerType: prop.providerType,
                  country: prop.country,
                  state: prop.state,
                  city: prop.city,
                  address: prop.address,
                  phone: prop.phone,
                  hasSmsPhone: !!prop.smsPhone,
                  smsPhone: prop.smsPhone,
                  website: <a href={`//${prop.website}`}>{prop.website}</a>,
                  notes: prop.notes,
                  telehealth: prop.telehealth ? "Yes" : "No",
                  actions: (
                    // we've added some custom button actions
                    <div className="actions-center">
                      {/* use this button to add a edit kind of action */}
                      <Link to={`/admin/provider/${prop.id}`}>
                        <Button
                          onClick={() => {}}
                          bsStyle="warning"
                          simple
                          icon
                        >
                          <i
                            className="pe-7s-pen action-icon"
                            style={{ color: "blue" }}
                          />
                        </Button>{" "}
                      </Link>
                      {/* use this button to remove the data row */}
                      <Button
                        onClick={() =>
                          onDeleteClickHandler(prop.name, prop.id)
                        }
                        bsStyle="danger"
                        simple
                        icon
                      >
                        <i className="pe-7s-trash action-icon" />
                      </Button>
                    </div>
                  ),
                };
              })}
              filterable
              columns={tableColumns}
              defaultPageSize={100}
              defaultFilterMethod={startsWithFilterMethod}
              defaultSorted={[{ id: "state", desc: false }]}
              showPaginationTop
              sortable
              loading={loading.source === FETCH_PROVIDER_LIST}
              className="-striped -highlight row-pointer"
              getTdProps={(state, rowInfo, column) => {
                if (column && ["website", "actions"].includes(column.id)) {
                  return {};
                } else if (rowInfo && rowInfo.row) {
                  return {
                    onClick: () =>
                      setHistoryNavigation({
                        enabled: true,
                        id: rowInfo.row.id,
                      }),
                  };
                } else {
                  return {};
                }
              }}
            />
          </div>
        }
      />
    </div>
  );
};

export default Providers;
